import type {
  Action,
  Habitat,
  MarketSlotIndex,
} from "@cloudnest/cascadia-engine";

/**
 * Typed replay-event adapter for Cascadia. Mirrors the persisted relay event
 * conversion in `multiplayer-game-store.ts` (`eventPayloadToAction`) so replay
 * reconstructs the exact same state the live client would on reconnect.
 *
 * Cascadia is a public-information game (no per-recipient viewer-state and no
 * game-validator projection), so replay renders canonical engine state
 * directly — there is nothing to redact.
 *
 * Most server-only events (`AutoWipeMarket`, `RefillMarket`) are NOT
 * reconstructed into actions: the live store ignores them during replay (their
 * effects are reproduced deterministically by the player actions + seeded
 * supply). `ScoreGame` is the exception for v2 terminal fixtures: it is a
 * server event, not an action, and the replay runner folds it through
 * `scoreGame` directly so winners and score breakdowns stay deterministic.
 */
export type CascadiaReplayEventType =
  | "init"
  | "DraftPair"
  | "DraftDecoupled"
  | "SpendNatureDecouple"
  | "SpendNatureWipe"
  | "WipeMarketOptional"
  | "PlaceTile"
  | "PlaceWildlife"
  | "ReturnWildlife"
  | "UnlockLandmark"
  | "ChooseLandmarkCard"
  | "SkipLandmark"
  | "EndTurn"
  | "ScoreGame";

export const CASCADIA_REPLAY_EVENT_TYPES = [
  "init",
  "DraftPair",
  "DraftDecoupled",
  "SpendNatureDecouple",
  "SpendNatureWipe",
  "WipeMarketOptional",
  "PlaceTile",
  "PlaceWildlife",
  "ReturnWildlife",
  "UnlockLandmark",
  "ChooseLandmarkCard",
  "SkipLandmark",
  "EndTurn",
  "ScoreGame",
] as const satisfies readonly CascadiaReplayEventType[];

export function normalizeCascadiaReplayEventType(
  eventType: string,
): CascadiaReplayEventType {
  if ((CASCADIA_REPLAY_EVENT_TYPES as readonly string[]).includes(eventType)) {
    return eventType as CascadiaReplayEventType;
  }
  throw new Error(`Unsupported Cascadia event type: ${eventType}`);
}

/**
 * Convert a persisted relay event (everything except `init`) into a typed
 * engine `Action`. Throws on an unsupported event type so a malformed fixture
 * fails loudly rather than silently dropping events. The `init` event carries a
 * full state snapshot, not an action, and is handled by the runner directly.
 */
export function cascadiaEventToAction(
  eventType: string,
  payload: Record<string, unknown>,
): Action {
  const type = normalizeCascadiaReplayEventType(eventType);
  const version = (payload.stateVersion as number | undefined) ?? 0;

  switch (type) {
    case "init":
      throw new Error("cascadiaEventToAction cannot convert the init event");
    case "ScoreGame":
      throw new Error(
        "cascadiaEventToAction cannot convert the ScoreGame server event",
      );
    case "DraftPair":
      return {
        type: "DraftPair",
        slot: payload.slot as MarketSlotIndex,
        stateVersion: version,
      };
    case "DraftDecoupled":
      return {
        type: "DraftDecoupled",
        tileSlot: payload.tileSlot as MarketSlotIndex,
        wildlifeSlot: payload.wildlifeSlot as MarketSlotIndex,
        stateVersion: version,
      };
    case "SpendNatureDecouple":
      return { type: "SpendNatureDecouple", stateVersion: version };
    case "SpendNatureWipe":
      return {
        type: "SpendNatureWipe",
        slots: payload.slots as readonly MarketSlotIndex[],
        stateVersion: version,
      };
    case "WipeMarketOptional":
      return { type: "WipeMarketOptional", stateVersion: version };
    case "PlaceTile":
      return {
        type: "PlaceTile",
        coord: payload.coord as { q: number; r: number },
        rotation: (payload.rotation as 0 | 1 | 2 | 3 | 4 | 5 | undefined) ?? 0,
        stateVersion: version,
      };
    case "PlaceWildlife":
      return {
        type: "PlaceWildlife",
        coord: payload.coord as { q: number; r: number },
        stateVersion: version,
      };
    case "ReturnWildlife":
      return { type: "ReturnWildlife", stateVersion: version };
    case "UnlockLandmark":
      return {
        type: "UnlockLandmark",
        habitat: payload.habitat as Habitat,
        stateVersion: version,
      };
    case "ChooseLandmarkCard":
      return {
        type: "ChooseLandmarkCard",
        cardId: payload.cardId as string,
        stateVersion: version,
      };
    case "SkipLandmark":
      return { type: "SkipLandmark", stateVersion: version };
    case "EndTurn":
      return { type: "EndTurn", stateVersion: version };
  }
}
